import { Component, OnInit, inject } from '@angular/core';
import { ModalContent, ModalRef } from '../../../modal-ref';
import {
  LimitPeriod, LimitStatus,
  MoneyLimitDefaultValues,
  resolvePeriodMessage
} from '../../../../services/user/data/user-limits.data';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { UserService } from '../../../../services/user/user.service';
import { UserLimitsService } from '../../../../services/user/user-limits.service';
import { CommonDataService } from '../../../../services/common-data.service';
import { FormsErrorHandlerService } from '../../../../services/forms-error-handler.service';
import { ToastMessageService } from '../../../../shared/modules/toast-message/toast-message.service';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslatePipe } from '../../../../services/translation/translate.pipe';
import { BtnComponent } from '../../../../shared/components/btn/btn.component';
import { FormInputComponent } from '../../../../shared/components/form-controls/form-input/form-input.component';
import { SelectModule } from 'src/app/core/shared/modules/select/select.module';


@Component({
    selector: 'app-create-update-limit',
    templateUrl: './create-update-limit.component.html',
    styleUrls: ['./create-update-limit.component.scss'],
    imports: [ReactiveFormsModule, FormInputComponent, SelectModule, BtnComponent, TranslatePipe]
})
export class CreateUpdateLimitComponent implements OnInit, ModalContent {
  private _fb = inject(FormBuilder);
  private _user = inject(UserService);
  private _limits = inject(UserLimitsService);
  private _data = inject(CommonDataService);
  private _formErrors = inject(FormsErrorHandlerService);
  private _toastMessage = inject(ToastMessageService);


  public modal: ModalRef;

  /**
   * Current modal info
   */
  public info;

  /**
   * Is edition mode
   */
  public edition: boolean;

  /**
   * Current limit (in edition mode only)
   */
  public limit;

  /**
   * Accounts for limit
   */
  public accounts: Array<any> = [];

  /**
   * Form errors
   */
  public errors: any = {};

  /**
   * Period values for limit
   */
  public periodValues = [
    {value: LimitPeriod.DAY, message: resolvePeriodMessage(LimitPeriod.DAY)},
    {value: LimitPeriod.WEEK, message: resolvePeriodMessage(LimitPeriod.WEEK)},
    {value: LimitPeriod.MONTH, message: resolvePeriodMessage(LimitPeriod.MONTH)},
  ];

  /**
   * Limits form
   */
  public form: FormGroup;

  ngOnInit(): void {
    this._initValues();
    this._resolveAccounts();
    this._initForm();
    this._setValuesForEdition();
  }

  get Status() { return LimitStatus; }

  /**
   * Handle period change
   */
  onPeriodChange() {
    if (this.edition) {
      return;
    }
    setTimeout(() => this._setDefaultValues());
  }

  /**
   * Cancel limit button handler
   */
  cancelLimit() {
    this._limits.deleteLimit(this.limit.id).pipe(
      tap(() => {
        this._toastMessage.success('t.limit-canceled-check-confirm');
        this.modal.close(true);
      }),
      catchError(error => {
        return of(error);
      })
    ).subscribe();
  }

  /**
   * Form submit handler
   */
  submitForm() {
    this.errors = this._formErrors.applyFormErrors(this.form, null, true);
    this.errors.accounts = this._formErrors.applyFormErrors(this.form.get('accounts') as FormGroup, null, true);

    if (this.form.invalid) {
      return;
    }

    const data = {
      period: this.form.get('period').value,
      type: this.modal.options.data.type,
      accounts: Object.keys(this.form.value.accounts).map(currency => ({
        currency,
        amount_cents: this._data.unitsToSubunits(this.form.value.accounts[currency], currency)
      }))
    };

    this._limits.createUpdateLimit(data).pipe(
      tap(() => {
        this._toastMessage.success('t.limit-success-updated');
        this.modal.close(true);
      }),
      catchError(error => {
        this.errors = this._formErrors.applyFormErrors(this.form, error.error);
        this.errors.accounts = this._formErrors.applyFormErrors(this.form.get('accounts') as FormGroup, error.error);

        if (error.error && error.error.errors && error.error.errors.base) {
          const message = error.error.errors.base[Object.keys(error.error.errors.base)[0]];
          this._toastMessage.error(message);
        }
        return of(error);
      })
    ).subscribe();
  }

  /**
   * Set current values for currencies
   *
   * @private
   */
  private _setCurrentValues() {
    const accountsGroup: AbstractControl = this.form.get('accounts');
    const keys: Array<string> = Object.keys(accountsGroup.value);

    keys.forEach(key => {
      const currency = this.limit.accounts.find(val => val.currency === key);
      accountsGroup.get(key).setValue(currency ? currency.amount : 0);
    });
  }

  /**
   * Set default values for currencies
   *
   * @private
   */
  private _setDefaultValues() {
    const accountsGroup: AbstractControl = this.form.get('accounts');
    const keys: Array<string> = Object.keys(accountsGroup.value);
    const period = this.form.get('period').value;
    const type = this.modal.options.data.type;

    keys.forEach(key => {
      accountsGroup.get(key).setValue(MoneyLimitDefaultValues[type][period][key] || 0);
    });
  }

  /**
   * Init data from modal
   *
   * @private
   */
  private _initValues() {
    this.info = this.modal.options.data.info;
    this.edition = !!this.modal.options.data.limit;
    this.limit = this.modal.options.data.limit || {};

    /**
     * Resolve available period options
     */
    if (!this.edition) {
      this.modal.options.data.list.pipe(
        tap((limits: any[]) =>
          this.periodValues = this.periodValues.filter(period => !limits.some(limit => limit.period === period.value))),
      ).subscribe();
    }
  }

  /**
   * Resolve user accounts to display
   *
   * @private
   */
  private _resolveAccounts() {
    this.accounts = this._user.accountWalletList;
  }

  /**
   * Init form with limits
   * @private
   */
  private _initForm() {
    this.form = this._fb.group({
      period: [null, Validators.required],
      accounts: this._getFormAccounts(),
    });
  }

  /**
   *
   * @private
   */
  private _getFormAccounts(): FormGroup {
    const group: FormGroup = this._fb.group({});

    this.accounts.forEach(account => {
      group.addControl(account.currency, this._fb.control(
        this._getValueForControl(account.currency),
        Validators.required
      ));
    });
    return group;
  }

  /**
   * Returns value for account control by currency
   *
   * @param currency
   * @private
   */
  private _getValueForControl(currency: string): number | null {
    if (!this.edition) {
      return null;
    } else {
      return (this.limit.accounts.find(account => account.currency === currency) || {}).amount || null;
    }
  }

  private _setValuesForEdition() {
    if (this.edition) {
      setTimeout(() => {
        this.form.get('period').setValue(this.limit.period);
        this.form.get('period').disable();
        this._setCurrentValues();
      });
    }
  }
}
